import env from '../../../../../core/config/env'
import { Box, Button, Chip, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import { useEffect, useRef, useState } from 'react'
import { useProductTickets } from '../../../../../core/hooks/useProductTickets'
import TicketController from '../../../../../core/controllers/TicketController'
import AlertDefault from '../../../../../components/Alerts'
import LoadingBackdrop from '../../../../../components/Loading/LoadingBackdrop'
import DataTableHeaderMobile from '../../../../../components/Tables/DataTableHeaderMobile'
import SuccessSnackbar from '../../../../../components/Snackbar/SuccessSnackbar'
import ErrorSnackbar from '../../../../../components/Snackbar/ErrorSnackbar'
import DataTable, {
  IDataTableColumn,
} from '../../../../../components/Tables/DataTable'
import DataTableHeaderWeb from '../../../../../components/Tables/DataTableHeaderWeb'
import TicketsFunctions from '../Tickets/TicketsFunctions'
import { IconEye, IconLink } from '../../../../../core/constants/IconConsts'
import ProductTicketDetail from '../Tickets/ProductTicketDetail'
import {
  TProductTicket,
  TicketStatusEnum,
} from '../../../../../core/types/Tickets'
import DataTableCellTypography from '../../../../../components/Typographys/DataTableCellTypography'
import DataTableSubcellTypography from '../../../../../components/Typographys/DataTableSubcellTypography'

const ProductTicketSales = ({
  productId,
}: {
  productId?: string
}): JSX.Element => {
  const CHECKIN_BASE_URL = env.TICKET_BASE_URL
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const oldSearch = useRef('')
  const [statusFilter, setStatusFilter] = useState<TicketStatusEnum | null>(
    TicketStatusEnum.Ready
  )

  const { loading, data, error, execute, clear } = useProductTickets({
    productId: productId ?? '',
    page,
    rows: rowsPerPage,
    search,
    status: statusFilter,
  })
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [errorSending, setErrorSending] = useState('')
  const [message, setMessage] = useState('')
  const [selectedTicket, setSelectedTicket] = useState<
    TProductTicket | undefined
  >()

  const handleSendTicket = async (orderCode: string) => {
    setErrorSending('')
    setMessage('')

    if (!orderCode) {
      setErrorSending('Nenhum pedido selecionado')
      return
    }

    setSending(true)

    try {
      const response = await TicketController.processOrResend({ orderCode })

      if (!response.success) {
        setErrorSending(response.error)
        return
      }

      setMessage(response.data || 'Ingresso enviado com sucesso.')
      execute()
    } finally {
      setSending(false)
    }
  }

  const handleResendPass = async (orderCode: string) => {
    setErrorSending('')
    setMessage('')

    if (!orderCode) {
      setErrorSending('Nenhum pedido selecionado')
      return
    }

    setSending(true)

    try {
      const response = await TicketController.resendCeletusPass({ orderCode })

      if (!response.success) {
        setErrorSending(response.error)
        return
      }

      setMessage(response.data || 'Reenvio ao Celetus Pass solicitado.')
      execute()
    } finally {
      setSending(false)
    }
  }

  const columns: readonly IDataTableColumn<TProductTicket>[] = [
    {
      id: 'orderCode',
      label: 'Cod. Pedido',
      render(id, value, row) {
        return (
          <Stack direction="row" alignItems="center">
            <Typography>{row.orderCode}</Typography>
          </Stack>
        )
      },
    },
    {
      id: 'offerName',
      label: 'Oferta',
      render(id, value) {
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              fontFamily={'Montserrat'}
              fontWeight={400}
              fontSize={'14px'}
              lineHeight={'18px'}
            >
              {value}
            </Typography>
          </Stack>
        )
      },
    },
    {
      id: 'buyerName',
      label: 'Cliente',
      width: 120,
      render(id, value, row) {
        return (
          <Stack direction="column" width="120px">
            <DataTableCellTypography>{value}</DataTableCellTypography>
            <DataTableSubcellTypography noWrap={true}>
              {row.buyerEmail}
            </DataTableSubcellTypography>
          </Stack>
        )
      },
    },
    { id: 'holderName', label: 'Atribuído' },
    {
      id: 'status',
      label: 'Status',
      render(id, value) {
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={TicketsFunctions.getStatusLabel(value)}
              size="small"
              sx={TicketsFunctions.getStatusChipStyles(value)}
            />
          </Stack>
        )
      },
    },
    {
      id: 'passSyncStatus',
      label: 'Integração Pass',
      render(id, value, row) {
        const label = TicketsFunctions.getPassSyncLabel(row.passSyncStatus)
        if (!label) {
          return (
            <Typography color="text.disabled" fontSize={'13px'}>
              —
            </Typography>
          )
        }
        const chip = (
          <Chip
            label={label}
            size="small"
            sx={TicketsFunctions.getPassSyncChipStyles(row.passSyncStatus)}
          />
        )
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            {row.passSyncError ? (
              <Tooltip title={row.passSyncError}>{chip}</Tooltip>
            ) : (
              chip
            )}
          </Stack>
        )
      },
    },
    {
      id: 'action',
      label: 'Ações',
      render(id, value, row) {
        const externalUrl = `${CHECKIN_BASE_URL}/ticket/3?ticket=${encodeURIComponent(row.codeId)}`
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Botão Visualizar Detalhes */}
            {value !== 'deleted' && (
              <Tooltip title="Visualizar ingresso">
                <Button
                  onClick={() => {
                    const ticket = data.items.find(
                      (x: TProductTicket) => x.id === row.id
                    )
                    setSelectedTicket(ticket)
                    setIsOpen(true)
                  }}
                  variant="text"
                  size="small"
                  sx={{
                    px: 1,
                    py: 0.5,
                    m: 0,
                    minWidth: 0,
                    borderRadius: '8px',
                  }}
                >
                  <img
                    src={IconEye}
                    alt="action-eye"
                    style={{ fontSize: '1.2rem', color: 'black' }}
                  />
                </Button>
              </Tooltip>
            )}

            <Tooltip title="Enviar ingresso">
              <Button
                onClick={() => handleSendTicket(row.orderCode)}
                variant="text"
                size="small"
                sx={{
                  px: 1,
                  py: 0.5,
                  m: 0,
                  minWidth: 0,
                  borderRadius: '8px',
                }}
              >
                <ConfirmationNumberOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'black' }}
                />
              </Button>
            </Tooltip>

            {/* Botão Reenviar ao Celetus Pass (só p/ rejeitado/enviando) */}
            {TicketsFunctions.canResendToPass(row.passSyncStatus) && (
              <Tooltip title="Reenviar ao Celetus Pass">
                <Button
                  onClick={() => handleResendPass(row.orderCode)}
                  variant="text"
                  size="small"
                  sx={{
                    px: 1,
                    py: 0.5,
                    m: 0,
                    minWidth: 0,
                    borderRadius: '8px',
                  }}
                >
                  <SyncOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'rgba(211, 47, 47, 1)' }}
                  />
                </Button>
              </Tooltip>
            )}

            {/* Botão Abrir página do participante */}
            <Tooltip title="Abrir edição do participante">
              <Button
                component="a"
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                size="small"
                sx={{
                  px: 1,
                  py: 0.5,
                  m: 0,
                  minWidth: 0,
                  borderRadius: '8px',
                }}
              >
                <img
                  src={IconLink} // <- define esse ícone (open in new tab / external link)
                  alt="action-open-external"
                  style={{ fontSize: '1.2rem', color: 'black' }}
                />
              </Button>
            </Tooltip>
          </Stack>
        )
      },
    },
  ]

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (rows: number) => {
    if (rows > 0) {
      setRowsPerPage(rows)
    }
    setPage(0)
  }

  useEffect(() => {
    if (
      data.rowsPerPage &&
      data.rowsPerPage > 0 &&
      data.rowsPerPage !== rowsPerPage
    ) {
      setRowsPerPage(data.rowsPerPage)
    }

    if (oldSearch.current !== search) {
      oldSearch.current = search
      setPage(0)
    }
  }, [data])

  useEffect(() => {
    setPage(0)
  }, [statusFilter])

  return (
    <Box width="100%" sx={{ p: 3 }}>
      {error !== '' && (
        <Box sx={{ mb: 2 }}>
          <AlertDefault onClose={clear} severity="error" sx={{ width: '100%' }}>
            {error}
          </AlertDefault>
        </Box>
      )}

      <Stack spacing={2} width="100%">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'start', sm: 'center' }}
          justifyContent={{ xs: 'start', sm: 'space-between' }}
          width="100%"
        >
          <Typography
            color="#343948"
            fontWeight={700}
            fontSize="16px"
            lineHeight="20px"
          >
            Ingressos Vendidos
          </Typography>
        </Stack>

        <Stack direction="column" spacing={1} sx={{ pt: 2 }}>
          <DataTable
            columns={columns}
            data={data}
            page={page}
            rowsPerPage={rowsPerPage}
            headerBackgroundColor="white"
            headerComponent={
              <DataTableHeaderWeb
                labelItems="ingressos vendidos"
                totalItems={data.total}
                setSearch={setSearch}
                executeSearch={execute}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                extraFilters={
                  <Select
                    displayEmpty
                    size="small"
                    value={statusFilter ?? ''}
                    onChange={(e) => {
                      const v = e.target.value
                      setStatusFilter(
                        v === '' ? null : (v as TicketStatusEnum)
                      )
                    }}
                    sx={{
                      minWidth: 160,
                      borderRadius: '8px',
                      backgroundColor: 'white',
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value={TicketStatusEnum.PendingData}>
                      Aguardando dados
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Ready}>
                      Pronto para uso
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Used}>Utilizado</MenuItem>
                    <MenuItem value={TicketStatusEnum.Canceled}>
                      Cancelado
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Transferred}>
                      Transferido
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Reserved}>
                      Aguardando pagamento
                    </MenuItem>
                  </Select>
                }
              />
            }
            headerMobileComponent={
              <DataTableHeaderMobile
                totalItems={data.total}
                setSearch={setSearch}
                executeSearch={execute}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                extraFilters={
                  <Select
                    displayEmpty
                    size="small"
                    value={statusFilter ?? ''}
                    onChange={(e) => {
                      const v = e.target.value
                      setStatusFilter(
                        v === '' ? null : (v as TicketStatusEnum)
                      )
                    }}
                    sx={{
                      width: '100%',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value={TicketStatusEnum.PendingData}>
                      Aguardando dados
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Ready}>
                      Pronto para uso
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Used}>Utilizado</MenuItem>
                    <MenuItem value={TicketStatusEnum.Canceled}>
                      Cancelado
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Transferred}>
                      Transferido
                    </MenuItem>
                    <MenuItem value={TicketStatusEnum.Reserved}>
                      Aguardando pagamento
                    </MenuItem>
                  </Select>
                }
              />
            }
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            hasNewCustomHeader
          />
        </Stack>
      </Stack>

      <LoadingBackdrop open={loading || sending} />

      <SuccessSnackbar open={message !== ''} onClose={() => setMessage('')}>
        {message}
      </SuccessSnackbar>

      <ErrorSnackbar
        open={errorSending !== ''}
        onClose={() => setErrorSending('')}
      >
        {errorSending}
      </ErrorSnackbar>

      <SuccessSnackbar open={success} onClose={() => setSuccess(false)}>
        Alterado com sucesso
      </SuccessSnackbar>

      {isOpen && selectedTicket && (
        <ProductTicketDetail
          open={isOpen}
          setOpen={setIsOpen}
          ticket={selectedTicket}
        />
      )}
    </Box>
  )
}

export default ProductTicketSales
